import { useState } from 'react';
import { Images, Maximize2 } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ReceiptImageViewer } from './ReceiptImageViewer';
import { ReceiptPdfViewer } from './ReceiptPdfViewer';
import { ExpenseForm, type ExpenseFormData } from './ExpenseForm';
import type { ExpenseWithDetails } from '@jobos/shared';

// Receipt on one side, form on the other — until the sides run out.
//
// Half of 390px is 195px, which is not a receipt and not a form either, so on a
// phone the split becomes a stack: the receipt is a band across the top, deep
// enough to tell one receipt from another, and the form gets the full width
// underneath it. Reading a total off the paper is what the expand control is
// for — one tap to the full screen and back.

interface ReceiptFormPanelProps {
  receiptPreviewUrl: string | null;
  receiptKind?: 'image' | 'pdf';
  projects?: { id: string; name: string }[];
  editingExpense?: ExpenseWithDetails | null;
  onSubmit: (data: ExpenseFormData) => void;
  onCancel: () => void;
  /**
   * Open the job's photo library to attach or swap this receipt. Absent leaves
   * the receipt alone — which is what the batch path wants, where the receipt
   * is the reason the form is open at all.
   */
  onChooseFromLibrary?: () => void;
  showCancel?: boolean;
  onBack?: () => void;
  onSkip?: () => void;
  onAdvance?: () => void;
  isPending?: boolean;
  batchPosition?: { isSaved: boolean; isLast: boolean };
}

export function ReceiptFormPanel({
  receiptPreviewUrl,
  receiptKind = 'image',
  projects,
  editingExpense,
  onSubmit,
  onCancel,
  onChooseFromLibrary,
  showCancel,
  onBack,
  onSkip,
  onAdvance,
  isPending,
  batchPosition,
}: ReceiptFormPanelProps) {
  const [expanded, setExpanded] = useState(false);

  const viewer =
    receiptKind === 'pdf' ? (
      <ReceiptPdfViewer src={receiptPreviewUrl ?? ''} />
    ) : (
      <ReceiptImageViewer src={receiptPreviewUrl ?? ''} />
    );

  return (
    <>
      <div className={receiptPreviewUrl ? 'sm:grid sm:grid-cols-2' : ''}>
        {receiptPreviewUrl && (
          // A share of the screen rather than a fixed height: the band has to
          // survive both a tall phone and a short one in a way `px` cannot.
          // From `sm` up it is a grid cell again and fills whatever the form
          // beside it makes the row.
          <div className="relative h-[35vh] sm:h-auto sm:min-h-[480px]">
            {viewer}
            <button
              type="button"
              onClick={() => setExpanded(true)}
              aria-label="Expand receipt"
              className="absolute right-2 top-2 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-black/50 text-white active:bg-black/70 sm:h-9 sm:w-9"
            >
              <Maximize2 className="h-5 w-5 sm:h-4 sm:w-4" />
            </button>
            {onChooseFromLibrary && (
              // On the receipt rather than under the form: this is about the
              // picture, and it reads as a caption to it.
              <button
                type="button"
                onClick={onChooseFromLibrary}
                className="absolute bottom-2 left-2 z-10 inline-flex items-center gap-1.5 rounded-full bg-black/50 px-3 py-2 text-xs font-medium text-white active:bg-black/70"
              >
                <Images className="h-4 w-4" aria-hidden="true" />
                Replace from job photos
              </button>
            )}
          </div>
        )}
        <div className="flex flex-col bg-white">
          {!receiptPreviewUrl && onChooseFromLibrary && (
            // An expense saved without a receipt: the drop zone is long gone by
            // the time anyone edits it, so this is the only way back to one.
            <div className="border-b px-4 pb-3 pt-4 text-center sm:px-5">
              <button
                type="button"
                onClick={onChooseFromLibrary}
                className="inline-flex min-h-[44px] items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 sm:min-h-0"
              >
                <Images className="h-4 w-4" aria-hidden="true" />
                Choose from job photos
              </button>
            </div>
          )}
          <ExpenseForm
            projects={projects}
            editingExpense={editingExpense}
            onSubmit={onSubmit}
            onCancel={onCancel}
            showCancel={showCancel}
            onBack={onBack}
            onSkip={onSkip}
            onAdvance={onAdvance}
            isPending={isPending}
            batchPosition={batchPosition}
          />
        </div>
      </div>

      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent
          mobileFullscreen
          className="flex h-[85vh] w-[90vw] max-w-5xl flex-col gap-0 overflow-hidden p-0 max-sm:overflow-y-hidden max-sm:p-0"
        >
          <DialogHeader className="border-b px-4 py-3 pr-14">
            <DialogTitle className="text-sm font-medium">Receipt</DialogTitle>
          </DialogHeader>
          {/* Mounted only while open so the expanded copy starts unzoomed and,
              for a PDF, is not a second iframe fetching in the background. */}
          <div className="min-h-0 flex-1">{expanded && viewer}</div>
        </DialogContent>
      </Dialog>
    </>
  );
}
